import { useStoreConfig } from "./storeConfig.mjs";
import { keysOf } from "../utils/collections.mjs";
import { unref } from "vue";
import { useHead, useRoute, useRuntimeConfig } from "#app";
import { useI18n } from "#i18n";
export function useAppSeoMeta(opts) {
  const {
    public: { appUrl }
  } = useRuntimeConfig();
  const route = useRoute();
  const {
    defaultTitle,
    defaultDescription,
    defaultKeywords,
    title_prefix,
    title_suffix
  } = useStoreConfig();
  const {
    locale: currentLocale,
    rt,
    tm,
    t
  } = useI18n({
    useScope: "global"
  });
  const alternateLocale = currentLocale.value === "en" ? "ar-EG" : "en-US";
  useHead(() => {
    const {
      title,
      description,
      image,
      keywords,
      type = "website"
    } = unref(opts || {});
    const imageToUse = image || `${appUrl}/maskable-icon-512x512.png`;
    const properties = {
      description: description || defaultDescription?.value || rt(tm("seo.description")).toString(),
      "og:title": title || defaultTitle?.value || rt(tm("seo.title")),
      "og:description": description || defaultDescription.value || rt(tm("seo.description")).toString(),
      "og:site_name": t("siteName").toString() || "",
      "og:type": `og:${type}`,
      "og:locale": currentLocale.value === "en" ? "en-US" : "ar-EG",
      "og:locale:alternate": alternateLocale,
      "og:url": `${appUrl}${route.path}`,
      "og:image": imageToUse,
      "twitter:card": "summary_large_image",
      "twitter:title": title || defaultTitle.value,
      "twitter:description": description || defaultDescription.value,
      "twitter:image": imageToUse,
      keywords: keywords || defaultKeywords.value
    };
    const meta = keysOf(properties).filter((prop) => properties[prop]).map((prop) => {
      return {
        hid: prop,
        name: prop,
        property: prop,
        content: properties[prop]
      };
    });
    return {
      title: title || defaultTitle.value,
      titleTemplate: `${title_prefix.value || ""} %s ${title_suffix.value || ""}`.trim(),
      meta
    };
  });
}
export function useSeoLinks() {
  const {
    public: { appUrl }
  } = useRuntimeConfig();
  const route = useRoute();
  const { locale: currentLocale } = useI18n();
  const seoLinks = [
    // Informs crawlers that this page is original page that holds this content
    {
      hid: "canonical",
      rel: "canonical",
      href: `${appUrl}${route.path}`
    },
    // Tells the crawler that there is an alternate `ar` locale mirror page for this page
    {
      hid: "alternate-hreflang-ar",
      rel: "alternate",
      hreflang: "ar",
      // adds the `ar` prefix only if the current locale is English so we don't end up with double `ar/ar`
      href: `${appUrl}${currentLocale.value === "en" ? "/ar" : ""}${route.path}`
    },
    // Tells the crawler that there is an alternate `en` locale mirror page for this page
    {
      hid: "alternate-hreflang-en",
      rel: "alternate",
      hreflang: "en",
      // removes the `ar` prefix so it correctly links to the English version
      href: `${appUrl}${route.path.replace(/^\/ar\//, "/")}`
    }
  ];
  useHead({
    link: seoLinks,
    htmlAttrs: {
      dir: currentLocale.value === "en" ? "ltr" : "rtl",
      lang: currentLocale.value
    }
  });
}
